import { List, Map, Set } from 'immutable';

import { addProfileId, shapeNewLinkedProductsForAPI, shapeNewProductForAPI } from '@/utils/product/shapeForAPI';
import { updateItemSpecificsForCategory, updateProductDetailsForCategory } from '@/utils/product/taxonomy';
import { shapeLinkedProductsOperations, shapeProductOperations } from '@/utils/product/shapeOperations';
import { shapeLinkedProductForApp, shapeLinkedProductForAPI } from '@/utils/product/linkedProducts';
import { getAIRequest, shapeAIResponseForApp } from '@/utils/ai';
import { canBeScheduled, getProductToSync } from '@/utils/product/statuses';
import { shapeProductForApp, sortShops } from '@/utils/product/shapeForApp';
import { shapeAiOperationsForApp } from '@/utils/profiles/ai';
import { shouldAddPlaceholder } from '@/utils/product/shouldAddPlaceholder';
import { getConnectedShopIds } from '@/utils/product/getConnectedShopIds';
import { shapeListingsForApp } from '@/utils/profiles/listings';
import { updateListingScore } from '@/utils/product/score';
import { getImagesMetadata } from '@/utils/photos';
import { setProductErrors } from '@/utils/product/errors';
import { createController } from '@/utils/controller';
import { getListingsPath } from '@/utils/listings/getListingsPath';
import { canConnectShop } from '@/utils/product/canConnectShop';
import { editNewProduct } from '@/utils/product/editNewProduct';
import { getPlaceholder } from '@/utils/listings/placeholders';
import { shapeForTable } from '@/utils/product/shapeForTable';
import { applyProfile } from '@/utils/product/applyProfile';
import { isVelaStatus } from '@/utils/listings/statuses';
import { editKeywords } from '@/utils/edit/keywords';
import { areComplete } from '@/utils/product/isComplete';
import { navigateTo } from '@/utils/navigation';
import { shapeId } from '@/utils/listings/listings';
import { getSize } from '@/utils/iterable/getSize';
import { api } from '@/utils/api';

import { AMOUNT, ENDPOINTS, ERROR, NEW, VALUE } from '@/constants/product';
import { EBAY, ETSY, FAIRE, SHOPIFY } from '@/constants/channels';
import { PAGE_SIZE, PROFILE_TYPE } from '@/constants/profiles';
import { MESSAGE, NOTIFICATION } from '@/constants/notifications';
import { POLLING_INTERVAL } from '@/constants/shops';
import { SCHEDULE_ACTION } from '@/constants/schedule';
import { EMPTY, VELA } from '@/constants';
import { ATTRIBUTE } from '@/constants/attributes';
import { FEATURE } from '@/constants/features';
import { STATUS } from '@/constants/listings';
import { MODALS } from '@/constants/modal';
import { FORM } from '@/constants/forms';

import actions from '@/actions';

const connectedProductsController = createController();
const productsController = createController();
const profileListingsController = createController();
const profileController = createController();

function abort(controller) {
  if (controller.isActive()) {
    controller.stop();
  }
}

function abortAll() {
  [
    connectedProductsController,
    productsController,
    profileListingsController,
    profileController,
  ].forEach(abort);
}

function* addPlaceholdersForNewListings(reduction, { products, shops, statuses }) {
  yield function effect(dispatch) {
    const shopsInEdit = Set(shops);
    const shopsData = reduction.getIn(['data', 'shopsData'], EMPTY.MAP);
    const userShops = reduction.get('shops');

    function addPlaceholder(shopId) {
      const product = products.get(shopId);
      const status = statuses.get(shopId);

      if (!product || status === STATUS[VELA].STAGING) return;

      const channel = product.get('channel');
      const shopData = shopsData.get(shopId);
      const shopIds = shopsInEdit.delete(shopId).toList();

      dispatch(
        actions.listings.addListingPlaceholder({
          placeholder: getPlaceholder({
            channel,
            product: shapeForTable({ product, shopData, shopIds, userShops }),
          }),
          shopId,
          status,
        })
      );
    }

    shops.forEach(addPlaceholder);
  };

  return reduction;
}

function* cancelEdit(reduction) {
  abortAll();

  if (!reduction.hasIn(['edit', 'session'])) {
    return reduction;
  }

  let edit = EMPTY.MAP;

  if (reduction.hasIn(['edit', 'syncStarted'])) {
    edit = edit.set('syncStarted', reduction.getIn(['edit', 'syncStarted']));
  }

  return reduction.set('edit', edit);
}

function* createNew(reduction, { product, shopId }) {
  const userFeatures = reduction.getIn(['user', 'features'], EMPTY.MAP);
  const userShops = reduction.get('shops');

  if (!userShops.hasIn(['byId', shopId])) return reduction;

  let edit = editNewProduct({
    data: reduction.get('edit').deleteAll(['reloadAfterSave', 'session', 'syncStarted']),
    product,
    shopId,
    userFeatures,
    userShops,
  });

  yield function effect(dispatch) {
    dispatch(actions.edit.getData());
  };

  if (reduction.hasIn(['edit', 'syncStarted'])) {
    edit = edit.set('syncStarted', reduction.getIn(['edit', 'syncStarted']));
  }

  if (reduction.hasIn(['edit', 'reloadAfterSave'])) {
    edit = edit.set('reloadAfterSave', reduction.getIn(['edit', 'reloadAfterSave']));
  }

  return reduction.set('edit', edit.set('session', reduction.getIn(['edit', 'session'])));
}

function* getData(reduction) {
  yield function effect(dispatch) {
    reduction.getIn(['edit', 'shops']).forEach(function getAllData(shopId) {
      dispatch(actions.data.getAllData(shopId));
    });
  };

  return reduction;
}

function* getLinkedProducts(reduction, { db, linkId, productId, shopId, signal: productsSignal }) {
  abort(connectedProductsController);

  yield function effect(dispatch) {
    const session = reduction.getIn(['edit', 'session']);
    const { signal } = connectedProductsController.start();
    api.products.getLinkedProducts({ db, linkId, productId, signal })
      .then(
        (products) => {
          dispatch(actions.edit.getLinkedProducts.fulfilled({ products, session, shopId, signal: productsSignal }));
        },
        (error) => dispatch(actions.edit.getLinkedProducts.rejected({ error, session, signal }))
      );
  };

  return reduction;
}

function* getLinkedProductsFulfilled(reduction, { products, session, shopId, signal }) {
  if (reduction.getIn(['edit', 'session']) !== session) {
    return reduction;
  }

  abort(connectedProductsController);
  const userFeatures = reduction.getIn(['user', 'features']);
  const userShops = reduction.get('shops');
  const linkedProducts = shapeLinkedProductForApp({ products, userFeatures, userShops });
  let edit = reduction.get('edit');

  if (getSize(linkedProducts) === 0 && getSize(edit.get('shops')) === 1) return reduction;

  function reduceProducts(result, linkedProduct) {
    result.shops = result.shops.push(linkedProduct.get('shopId'));
    result.actions.push(
      actions.edit.getProductData({
        channel: userShops.getIn(['byId', linkedProduct.get('shopId'), 'channel']),
        db: userShops.getIn(['byId', linkedProduct.get('shopId'), 'db']),
        productId: linkedProduct.get('productId'),
        shopId: linkedProduct.get('shopId'),
        signal,
      })
    );

    return result;
  }

  const {
    actions: actionsToDispatch,
    shops,
  } = linkedProducts.reduce(reduceProducts, { actions: [], shops: List([shopId]) });

  if (getSize(actionsToDispatch) > 0) {
    yield function effect(dispatch) {
      actionsToDispatch.forEach(dispatch);
    };
  }

  edit = edit
    .set('linkedProducts', linkedProducts)
    .set('shops',
      shops.sort(
        sortShops({
          first: edit.get('shopId'),
          prioritized: getConnectedShopIds(edit.get('products')),
          sorted: userShops.get('options'),
        })
      )
    );

  if (edit.has('activeShopId') && !edit.get('shops').includes(edit.get('activeShopId'))) {
    edit = edit.set('activeShopId', edit.get('shopId'));
  }

  return reduction.set('edit', edit);
}

function* getLinkedProductsRejected(reduction, { error, session, signal }) {
  if (signal.aborted || reduction.getIn(['edit', 'session']) !== session) {
    return reduction;
  }

  abort(connectedProductsController);

  yield function effect() {
    console.error('Error encountered while getting linked product: ', error);
    navigateTo(getListingsPath(reduction));
  };

  return reduction.set('edit', EMPTY.MAP);
}

function* getProduct(reduction, action = {}) {
  abort(connectedProductsController);
  abort(productsController);

  const {
    productId = reduction.getIn(['edit', 'productId']),
    shopId = reduction.getIn(['edit', 'shopId']),
  } = action;

  const shop = reduction.getIn(['shops', 'byId', shopId]);

  if (!productId || !shop) {
    yield function effect(dispatch) {
      dispatch(actions.edit.cancelEdit());
    };

    return reduction;
  }

  const db = shop.get('db');
  const channel = shop.get('channel');
  const linkId = reduction.getIn(['edit', 'linkId']);
  const { signal } = productsController.start();

  yield function effect(dispatch) {
    dispatch(actions.edit.getProductData({ channel, db, productId, shopId, signal }));

    if (linkId) {
      dispatch(actions.edit.getLinkedProducts({ db, linkId, productId, shopId, signal }));
    }

    if (getSize(reduction.getIn(['edit', 'shops'])) > 0) {
      dispatch(actions.edit.getData());
    }
  };

  return reduction.deleteIn(['edit', 'linkedProducts']);
}

function* getProductData(reduction, { asProfile, channel, db, productId, shopId, signal }) {
  function isPhotoScoringEnabled() {
    if (reduction.getIn(['shops', 'byId', shopId, 'isVelaOnlyShop'])) return false;

    switch (channel) {
      case EBAY:
      case FAIRE: {
        return (
          reduction.getIn(['user', 'features', FEATURE.PHOTO_SCORING]) &&
          reduction.getIn(['user', 'features', FEATURE.AI_CREDITS])
        );
      }

      case ETSY:
      case SHOPIFY: {
        return reduction.getIn(['user', 'features', FEATURE.PHOTO_SCORING]);
      }

      default: {
        return false;
      }
    }
  }

  yield function effect(dispatch) {
    dispatch(actions.data.getAllData(shopId));

    const session = reduction.getIn(['edit', 'session']);

    api.products.getById({
      channel,
      db,
      getImagesMetadata: isPhotoScoringEnabled() ? getImagesMetadata : undefined,
      payload: { id: [productId] },
      shopId,
      signal,
    })
      .then(
        (response) => {
          dispatch(
            actions.edit.getProductData.fulfilled({
              asProfile,
              channel,
              db,
              productId,
              response,
              session,
              shopId,
              signal,
            })
          );
        },
        (error) => {
          if (asProfile) {
            dispatch(actions.edit.setProfile.rejected({ error, session, shopId, signal }));
          } else {
            dispatch(actions.edit.getProductData.rejected({ error, session, signal }));
          }
        }
      );
  };

  return reduction;
}

function* getProductDataFulfilled(reduction, { asProfile, channel, db, productId, response, session, shopId, signal }) {
  if (reduction.getIn(['edit', 'session']) !== session) {
    return reduction;
  }

  let data = reduction.get('edit');

  if (!data.get('productId')) return reduction;

  if (!getSize(response?.products)) {
    yield function effect(dispatch) {
      const error = `Product "${productId}" was not found in the shop "${shopId}" at DB "${db}"`;

      if (asProfile) {
        dispatch(actions.edit.setProfile.rejected({ error, session, shopId, signal }));
      } else {
        dispatch(actions.edit.getProductData.rejected({ error, session, signal }));
      }
    };

    return reduction;
  }

  const userFeatures = reduction.getIn(['user', 'features'], EMPTY.MAP);
  const shopData = reduction.getIn(['data', 'shopsData', shopId], EMPTY.MAP);
  const marketplaceId = reduction.getIn(['shops', 'byId', shopId, 'marketplaceId']);
  const shop = reduction.getIn(['shops', 'byId', shopId]);
  let product;

  try {
    product = shapeProductForApp({ asProfile, data: response, shop, shopData, userFeatures });
  } catch (error) {
    yield function effect(dispatch) {
      if (asProfile) {
        dispatch(actions.edit.setProfile.rejected({ error, session, shopId, signal }));
      } else {
        dispatch(actions.edit.getProductData.rejected({ error, session, signal }));
      }
    };

    return reduction;
  }

  if (channel === EBAY) {
    const category = product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE]);

    if (
      category &&
      category !== AMOUNT.ZERO &&
      !reduction.hasIn(['data', 'loaded', 'taxonomy', channel, marketplaceId, category])
    ) {
      product = product.set('loading', true);

      yield function effect(dispatch) {
        dispatch(actions.data.getTaxonomyData({ category, channel, marketplaceId }));
      };
    }
  }

  function updateStatuses(statuses = EMPTY.MAP) {
    return statuses.set(shopId, product.get('status'));
  }

  const userShops = reduction.get('shops');

  if (asProfile) {
    const profile = reduction.getIn(['edit', 'products', shopId, 'profile']);

    if (channel === EBAY && profile.getIn(['sections', FORM.DETAILS])) {
      const category = product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE]);

      if (!reduction.hasIn(['data', 'loaded', 'taxonomy', channel, marketplaceId, category])) {
        yield function effect(dispatch) {
          dispatch(actions.data.getTaxonomyData({ category, channel, marketplaceId }));
        };

        return reduction
          .setIn(['edit', 'products', shopId, 'profile', 'template'], product)
          .setIn(['edit', 'waitingForCategoriesToLoad', shopId], Set([category]));
      }
    }

    yield function effect(dispatch) {
      dispatch(actions.edit.setProfile.fulfilled({ session, shopId, type: PROFILE_TYPE.STANDARD }));
    };

    return reduction.setIn(['edit', 'products', shopId, 'profile', 'template'], product);
  } else {
    const currentShopId = data.get('shopId');

    if (canConnectShop({ connectedShops: data.get('shops', EMPTY.LIST), userFeatures, userShops })(shopId)) {
      data = data.set('shops',
        data
          .get('shops', EMPTY.LIST)
          .push(shopId)
          .sort(
            sortShops({
              fitst: currentShopId,
              prioritized: getConnectedShopIds(data.get('products')).add(shopId),
              sorted: userShops.get('options'),
            })
          )
      );
    }

    if (shopId === currentShopId && data.get('loading')) {
      if (product.get('linkId') && !data.get('linkId')) {
        data = data.set('shops',
          data
            .get('shops', EMPTY.LIST)
            .concat(
              product
                .get('linkedProductsShopIds')
                .filter(canConnectShop({ connectedShops: data.get('shops', EMPTY.LIST), userFeatures, userShops }))
            )
            .sort(
              sortShops({
                first: currentShopId,
                prioritized: getConnectedShopIds(data.get('products')).add(shopId),
                sorted: userShops.get('options'),
              })
            )
        );

        if (getSize(data.get('shops')) > 1) {
          yield function effect(dispatch) {
            dispatch(
              actions.edit.getLinkedProducts({
                db,
                linkId: data.get('linkId'),
                productId: data.get('productId'),
                shopId,
                signal,
              })
            );
          };
        }
      }

      data = data
        .delete('loading')
        .set('title', product.getIn([ATTRIBUTE.TITLE, VALUE]))
        .set('status', product.get('status'))
        .set('channelProductId', product.get('channelProductId'))
        .set('linkId', product.get('linkId'))
        .set('channel', product.get('channel'))
        .set('isComplete', !getSize(product.get(ERROR)))
        .set('scheduledAt', product.get('scheduledAt'))
        .set('thumbnailUrl', product.getIn([ATTRIBUTE.PHOTOS, VALUE, 0, 'fullsize_url']));
    }

    if (getSize(data.get('shops')) === getSize(data.get('products', EMPTY.MAP).delete(shopId)) + 1) {
      abort(productsController);

      if (data.get('reloadAfterSave')) {
        data = data.delete('reloadAfterSave');
      }
    }
  }

  data = data
    .setIn(['products', shopId], product)
    .update('statuses', updateStatuses);

  return reduction.set('edit', data.set('isComplete', areComplete(data.get('products'))));
}

function* getProductDataRejected(reduction, { error, session, signal }) {
  console.error('Error encountered while getting product: ', error);

  if (signal.aborted || reduction.getIn(['edit', 'session']) !== session) {
    return reduction;
  }

  if (!reduction.getIn(['edit', 'reloadAfterSave'])) {
    yield function effect() {
      navigateTo(getListingsPath(reduction));
    };

    return reduction.set('edit', EMPTY.MAP);
  }

  yield function effect(dispatch) {
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return reduction;
}

function* getProfileListings(state, shopId) {
  abort(profileListingsController);

  if (!shopId || !state.hasIn(['edit', 'products', shopId, 'profile'])) return state;

  const profile = state.getIn(['edit', 'products', shopId, 'profile']);
  const profileId = profile.get('id');
  const listings = profile.get('listings');
  const page = listings.get('page', -1) + 1;
  const limit = PAGE_SIZE;
  const offset = page * limit;

  if (offset >= listings.get('total')) return state;

  yield function effect(dispatch) {
    const session = state.getIn(['edit', 'session']);
    const { signal } = profileListingsController.start();

    api.profiles.getListings({ limit, offset, profileId, signal, type: profile.get('type') })
      .then(function onFulfilled(response) {
        dispatch(actions.edit.getProfileListings.fulfilled({ page, session, shopId, response }));
      })
      .catch(function onRejected(error) {
        dispatch(actions.edit.getProfileListings.rejected({ error, session, shopId, signal }));
      });
  };

  return state.setIn(['edit', 'products', shopId, 'profile', 'listings', 'loading'], true);
}

function* getProfileListingsFulfilled(state, { page, session, response, shopId }) {
  if (state.getIn(['edit', 'session']) !== session || !state.hasIn(['edit', 'products', shopId, 'profile'])) {
    return state;
  }

  abort(profileListingsController);

  function updateListings(listings) {
    return shapeListingsForApp({ response, state: listings.delete('loading').set('page', page) });
  }

  return state.updateIn(['edit', 'products', shopId, 'profile', 'listings'], updateListings);
}

function* getProfileListingsRejected(state, { error, session, shopId, signal }) {
  if (
    signal.aborted ||
    state.getIn(['edit', 'session']) !== session ||
    !state.hasIn(['edit', 'products', shopId, 'profile'])
  ) {
    return state;
  }

  abort(profileListingsController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_LISTINGS,
      })
    );
  };

  return state.deleteIn(['edit', 'products', shopId, 'profile', 'listings', 'loading']);
}

function* removeNew(reduction, shopId) {
  let data = reduction.get('edit');

  function filterShopIds(item) {
    return item !== shopId;
  }

  function removeFromShops(shops) {
    return shops.filter(filterShopIds);
  }

  function removeByShopId(items = EMPTY.MAP) {
    return items.delete(shopId);
  }

  data = data
    .update('shops', removeFromShops)
    .update('products', removeByShopId)
    .update('statuses', removeByShopId);

  if (data.get('activeShopId') === shopId) {
    data = data.set('activeShopId', data.get('shopId'));
  }

  return reduction.set('edit', data.set('isComplete', areComplete(data.get('products'))));
}

function* saveAs(reduction, { confirmed, scheduleAt, statuses }) {
  const edit = reduction.get('edit');
  const userShops = reduction.get('shops');
  const userFeatures = reduction.getIn(['user', 'features']);
  const shopsData = reduction.getIn(['data', 'shopsData'], EMPTY.MAP);

  function reduceShops(result, shopId) {
    const { edit: data, indicator, product } = getProductToSync({
      edit: result.edit || edit,
      scheduleAt,
      shopId,
      shopsData,
      statuses,
      userFeatures,
      userShops,
    });

    if (data) {
      result.edit = data;
    } else {
      result.products = result.products.set(shopId, product);

      if (indicator) {
        result.actions.push(actions.shops.setData({ path: ['syncData', shopId], value: Map({ indicator }) }));
      }
    }

    return result;
  }

  const { actions: actionsToDispatch, edit: data, products } = edit
    .get('shops')
    .reduce(reduceShops, { actions: [], products: EMPTY.MAP });

  if (data) {
    return reduction.set('edit', data);
  }

  if (confirmed && edit.getIn(['modal', 'type']) === MODALS.CONFIRMATIONS.PUBLISH) {
    actionsToDispatch.push(actions.edit.setModal());
  }

  yield function effect(dispatch) {
    if (getSize(actionsToDispatch) > 0) {
      actionsToDispatch.forEach(dispatch);
    }

    dispatch(
      actions.shops.rescheduleShopsPolling(
        reduction.getIn(['user', 'config', 'shopsPollingIntervalShort']) ||
        POLLING_INTERVAL.SHORT
      )
    );

    if (edit.get('productId') === NEW) {
      dispatch(actions.edit.saveNew({ edit, products, scheduleAt, statuses }));
    } else {
      dispatch(actions.edit.saveToChannels({ edit, products, scheduleAt, statuses }));
    }
  };

  return reduction.setIn(['edit', 'products'], EMPTY.MAP).setIn(['edit', 'syncStarted'], true);
}

function* saveNew(reduction, { edit, products, scheduleAt, statuses }) {
  yield function effect(dispatch) {
    const isLinkedProduct = getSize(edit.get('shops')) > 1;
    const shopId = edit.get('shopId');
    const db = reduction.getIn(['shops', 'byId', shopId, 'db']);
    const channel = reduction.getIn(['shops', 'byId', shopId, 'channel']);
    const userFeatures = reduction.getIn(['user', 'features'], EMPTY.MAP);
    const request = { params: { db }};

    if (isLinkedProduct) {
      const userShops = reduction.get('shops');
      request.payload = shapeNewLinkedProductsForAPI({
        products,
        scheduleAt,
        shopId,
        statuses,
        userFeatures,
        userShops,
      });

      request.url = `/shop/${shopId}/${ENDPOINTS.CREATE.LINKED}`;
    } else {
      const status = statuses.get(shopId);
      const product = products.get(shopId);

      request.payload = {
        saveOnly: isVelaStatus({ channel, status }),
        operations: shapeNewProductForAPI({ product, shopId, status, scheduleAt, userFeatures }),
      };

      request.url = `/shops/${shopId}/${ENDPOINTS.CREATE[channel]}`;

      if (product.has('profile')) {
        request.payload = addProfileId({ operation: request.payload, product });
      }
    }

    api
      .post(request)
      .then(
        (response) => dispatch(actions.edit.saveNew.fulfilled({ edit, products, response, shopId, statuses })),
        (error) => dispatch(actions.edit.saveNew.rejected({ edit, error, products }))
      );
  };

  return reduction;
}

function* saveNewFulfilled(reduction, { edit, products, response, shopId, statuses }) {
  switch (response?.result) {
    case 'ok':
    case 'succeeded': {
      yield function effect(dispatch) {
        const status = statuses.get(shopId);

        if (status === STATUS[VELA].SCHEDULED) {
          dispatch(
            actions.notifications.add({
              type: NOTIFICATION.SUCCESS,
              message: MESSAGE.SUCCESS.SCHEDULE_PUBLICATION,
            })
          );

          if (reduction.getIn(['edit', 'session']) === edit.get('session')) {
            dispatch(actions.edit.setData({ status }));
          }
        } else {
          dispatch(actions.edit.addPlaceholdersForNewListings({ products, shops: edit.get('shops'), statuses }));

          if (reduction.getIn(['edit', 'session']) === edit.get('session')) {
            navigateTo(getListingsPath(reduction.setIn(['listings', 'status'], status)));
          }
        }
      };

      if (reduction.getIn(['edit', 'session']) === edit.get('session')) {
        return reduction.setIn(['edit', 'reloadAfterSave'], true);
      }

      return reduction;
    }

    default: {
      yield function effect(dispatch) {
        dispatch(actions.edit.saveNew.rejected({ error: response?.error }));
      };

      return reduction;
    }
  }
}

function* saveNewRejected(reduction, { edit, error, products }) {
  yield function effect(dispatch) {
    function clearSyncData(product, shopId) {
      dispatch(actions.shops.setData({ path: ['syncData', shopId] }));
    }

    console.error(error || 'Unknown error encountered when posting new listing');

    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.CREATE_LISTINGS,
      })
    );

    products.forEach(clearSyncData);
  };

  if (reduction.getIn(['edit', 'session']) === edit.get(['session'])) {
    return reduction.setIn(['edit', 'products'], edit.get('products'));
  }

  return reduction;
}

function* saveToChannels(reduction, { edit, products, scheduleAt, statuses }) {
  yield function effect(dispatch) {
    const userShops = reduction.get('shops');

    function reduceLinkedProducts(result, product) {
      result.push(shapeLinkedProductForAPI({ product, userShops }));
      return result;
    }

    function clearSyncData(product, shopId) {
      dispatch(actions.shops.setData({ path: ['syncData', shopId] }));
    }

    const currentShopId = edit.get('shopId');
    const currentShop = userShops.getIn(['byId', currentShopId]);
    const currentChannel = currentShop.get('channel');
    const currentDb = currentShop.get('db');
    const shops = edit.get('shops');
    const isLinkedProduct = getSize(shops) > 1;
    const schedule = scheduleAt !== undefined && canBeScheduled(products.get(currentShopId));
    const scheduleAction = schedule
      ? scheduleAt
        ? products.getIn([currentShopId, 'scheduledAt'])
          ? SCHEDULE_ACTION.RESCHEDULE
          : SCHEDULE_ACTION.SCHEDULE
        : SCHEDULE_ACTION.CANCEL
      : undefined;

    const cancel = (
      scheduleAction === SCHEDULE_ACTION.CANCEL && statuses.get(currentShopId) === STATUS[VELA].SCHEDULED
    );

    if (isLinkedProduct) {
      const userFeatures = reduction.getIn(['user', 'features'], EMPTY.MAP);
      const linkId = edit.get('linkId');
      const payload = {
        db: currentDb,
        linkId,
        shopId: currentShopId,
        ...shapeLinkedProductsOperations({
          linkId,
          products,
          scheduleAt,
          shopId: currentShopId,
          statuses,
          userFeatures,
          userShops,
        }),
      };

      if (!getSize(payload.newProducts) && !getSize(payload.productsToEdit)) {
        products.forEach(clearSyncData);
        dispatch(actions.edit.getProduct());
        return;
      }

      if (getSize(payload.newProducts)) {
        payload.existingProducts = edit
          .get('linkedProducts', EMPTY.LIST)
          .reduce(
            reduceLinkedProducts,
            [shapeLinkedProductForAPI({ product: products.get(currentShopId), userShops })],
          );
      }

      products.forEach(function forEachProduct(product, shopId) {
        const productId = product.get('productId');
        const status = statuses.get(shopId);

        if (status !== STATUS[VELA].SCHEDULED && getSize(product.get('operations'))) {
          dispatch(actions.listings.toggleState({ listings: [productId], state: ['processing', shopId] }));

          if (shouldAddPlaceholder({ product, status })) {
            dispatch(actions.edit.addPlaceholdersForNewListings({ products, shops: List([shopId]), statuses }));
          }
        }

        if (productId !== NEW && product.has('profile')) {
          const profileId = product.getIn(['profile', 'id']);
          const profileType = product.getIn(['profile', 'type']);

          api.profiles.connectListings({ listings: [productId], profileId, type: profileType })
            .then(() => dispatch(actions.data.getProfiles({ force: true, shopId, type: profileType })))
            .catch((error) => {
              console.error(error);
              dispatch(
                actions.notifications.add({
                  type: NOTIFICATION.ERROR,
                  message: MESSAGE.FAIL.CONNECT_PROFILE,
                })
              );
            });
        }
      });

      api.products.editLinkedProducts(payload)
        .then(
          (response) => {
            dispatch(
              actions.edit.saveToChannels.fulfilled({ cancel, edit, linkId: response.linkId, scheduleAction })
            );
          },
          (error) => dispatch(actions.edit.saveToChannels.rejected({ edit, error, products, scheduleAction }))
        );
    } else {
      const product = products.get(currentShopId);
      const operations = shapeProductOperations({ product, scheduleAt });

      if (getSize(operations) === 0) {
        dispatch(actions.edit.getProduct());
        clearSyncData(product, currentShopId);
        return;
      }

      const productId = product.get('productId');
      const status = statuses.get(currentShopId);

      if (status !== STATUS[VELA].SCHEDULED) {
        dispatch(actions.listings.toggleState({ listings: [productId], state: ['processing', currentShopId] }));

        if (shouldAddPlaceholder({ product, status })) {
          dispatch(actions.edit.addPlaceholdersForNewListings({ products, shops: List([currentShopId]), statuses }));
        }
      }

      if (productId !== NEW && product.has('profile')) {
        const profileId = product.getIn(['profile', 'id']);
        const profileType = product.getIn(['profile', 'type']);

        api.profiles.connectListings({ listings: [productId], profileId, type: profileType })
          .then(() => dispatch(actions.data.getProfiles({ force: true, shopId: currentShopId, type: profileType })))
          .catch((error) => {
            console.error(error);
            dispatch(
              actions.notifications.add({
                type: NOTIFICATION.ERROR,
                message: MESSAGE.FAIL.CONNECT_PROFILE,
              })
            );
          });
      }

      api.products.editSingleProduct({
        channel: currentChannel,
        db: currentDb,
        operations,
        shopId: currentShopId,
        status,
      })
        .then(
          () => dispatch(actions.edit.saveToChannels.fulfilled({ cancel, edit, scheduleAction })),
          (error) => dispatch(actions.edit.saveToChannels.rejected({ edit, error, products, scheduleAction }))
        );
    }
  };

  return reduction;
}

function* saveToChannelsFulfilled(reduction, { cancel, edit, linkId, scheduleAction }) {
  if (scheduleAction) {
    yield function effect(dispatch) {
      const actionMessage = {
        [SCHEDULE_ACTION.RESCHEDULE]: MESSAGE.SUCCESS.RESCHEDULE_PUBLICATION,
        [SCHEDULE_ACTION.SCHEDULE]: MESSAGE.SUCCESS.SCHEDULE_PUBLICATION,
        [SCHEDULE_ACTION.CANCEL]: MESSAGE.SUCCESS.CANCEL_PUBLICATION,
      };

      dispatch(actions.notifications.add({ type: NOTIFICATION.SUCCESS, message: actionMessage[scheduleAction] }));
    };
  }

  if (reduction.getIn(['edit', 'session']) === edit.get('session')) {
    yield function effect(dispatch) {
      if (cancel) {
        dispatch(actions.edit.setData({ cancel: true }));
      } else {
        if (linkId) {
          dispatch(actions.edit.setData({ linkId: shapeId(linkId) }));
        }

        dispatch(actions.edit.setData({ loading: true }));
      }
    };

    return reduction.setIn(['edit', 'reloadAfterSave'], true);
  }

  return reduction;
}

function* saveToChannelsRejected(reduction, { edit, error, products, scheduleAction }) {
  yield function effect(dispatch) {
    function clearSyncData(product, shopId) {
      dispatch(actions.shops.setData({ path: ['syncData', shopId] }));
    }

    console.error('Error encountered while syncing updates: ', error);

    products.forEach(clearSyncData);

    if (scheduleAction) {
      const actionMessage = {
        [SCHEDULE_ACTION.RESCHEDULE]: MESSAGE.FAIL.RESCHEDULE_PUBLICATION,
        [SCHEDULE_ACTION.SCHEDULE]: MESSAGE.FAIL.SCHEDULE_PUBLICATION,
        [SCHEDULE_ACTION.CANCEL]: MESSAGE.FAIL.CANCEL_PUBLICATION,
      };

      dispatch(actions.notifications.add({ type: NOTIFICATION.ERROR, message: actionMessage[scheduleAction] }));
    } else {
      dispatch(actions.notifications.add({ message: MESSAGE.FAIL.SYNC, type: NOTIFICATION.ERROR }));
    }
  };

  if (reduction.getIn(['edit', 'session']) === edit.get('session')) {
    return reduction.setIn(['edit', 'products'], edit.get('products'));
  }

  return reduction;
}

function* setData(state, data) {
  let edit = state.get('edit');

  for (const [key, value] of Object.entries(data)) {
    edit = value === undefined
      ? edit.delete(key)
      : edit.set(key, value);
  }

  return state.set('edit', edit);
}

function* setModal(reduction, payload) {
  return payload
    ? reduction.setIn(['edit', 'modal'], Map(payload))
    : reduction.deleteIn(['edit', 'modal']);
}

function* setProduct(reduction, { product, shopId }) {
  if (getSize(reduction.get('edit')) === 0) return reduction;

  const edit = reduction.get('edit').setIn(['products', shopId], product);

  return reduction.set('edit', edit.set('isComplete', areComplete(edit.get('products'))));
}

function* setProfile(reduction, payload) {
  abort(profileController);
  abort(profileListingsController);

  const { profile, shopId } = payload;

  if (!profile) {
    return reduction.setIn(['edit', 'products', shopId], reduction.getIn(['edit', 'products', shopId, 'oldProduct']));
  }

  const type = profile.get('type');

  switch (type) {
    case PROFILE_TYPE.AI: {
      yield function effect(dispatch) {
        const session = reduction.getIn(['edit', 'session']);
        const { signal } = profileController.start();

        api.profiles.getById({ profileId: profile.get('id'), signal, type })
          .then(
            (response) => dispatch(actions.edit.setProfile.fulfilled({ ...payload, response, session, type })),
            (error) => dispatch(actions.edit.setProfile.rejected({ error, session, shopId, signal })),
          );
      };

      break;
    }

    case PROFILE_TYPE.STANDARD: {
      yield function effect(dispatch) {
        const channel = reduction.getIn(['shops', 'byId', shopId, 'channel']);
        const db = reduction.getIn(['shops', 'byId', shopId, 'db']);
        const productId = profile.get('templateId');
        const { signal } = profileController.start();
        dispatch(actions.edit.getProductData({ asProfile: true, channel, db, productId, shopId, signal }));
      };

      break;
    }

    default: {
      return reduction;
    }
  }

  let product = reduction.getIn(['edit', 'products', shopId]);

  if (!product.has('oldProduct')) {
    product = product.set('oldProduct', product);
  }

  return reduction.setIn(['edit', 'products', shopId], product.set('loading', true).set('profile', profile));
}

function* setProfileFulfilled(reduction, { session, shopId, type, ...payload }) {
  if (reduction.getIn(['edit', 'session']) !== session) {
    return reduction;
  }

  abort(profileController);

  const userFeatures = reduction.getIn(['user', 'features'], EMPTY.MAP);
  const shopData = reduction.getIn(['data', 'shopsData', shopId], EMPTY.MAP);
  const shop = reduction.getIn(['shops', 'byId', shopId]);
  const channel = shop.get('channel');
  const edit = reduction.get('edit');

  function clearCategoriesToLoad(state) {
    const categoriesToLoad = state.get('waitingForCategoriesToLoad', EMPTY.MAP).delete(shopId);

    return getSize(categoriesToLoad) > 0
      ? state.set('waitingForCategoriesToLoad', categoriesToLoad)
      : state.delete('waitingForCategoriesToLoad');
  }

  switch (type) {
    case PROFILE_TYPE.AI: {
      const oldProduct = reduction.getIn(['edit', 'products', shopId, 'oldProduct']);
      const profile = payload.profile.set('ai', shapeAiOperationsForApp(payload.response.data));
      const applied = applyProfile({
        product: oldProduct,
        profile,
        shop,
        shopData,
        type,
        userFeatures,
      });
      applied.product = applied.product.set('oldProduct', oldProduct).set('profile', profile);
      return reduction.set('edit', edit.setIn(['products', shopId], applied.product));
    }

    case PROFILE_TYPE.STANDARD: {
      const profile = edit.getIn(['products', shopId, 'profile']);

      if (!profile) {
        return reduction;
      }

      const oldProduct = edit.getIn(['products', shopId, 'oldProduct']);
      const applied = payload.applied || applyProfile({
        product: oldProduct,
        profile,
        shop,
        shopData,
        template: profile.get('template'),
        type: PROFILE_TYPE.STANDARD,
        userFeatures,
      });

      applied.product = applied.product.set('oldProduct', oldProduct).set('profile', profile);

      if (applied.confirmation) {
        yield function effect(dispatch) {
          dispatch(
            actions.edit.setModal({
              product: applied.product,
              restore: true,
              shopId,
              type: applied.confirmation,
            })
          );
        };

        return reduction;
      }

      if (
        !payload.applied &&
        channel === SHOPIFY &&
        oldProduct.has(ATTRIBUTE.KEYWORDS) &&
        applied.product.getIn([FORM.PHOTOS, VALUE, 0, 'alt_text']) &&
        applied.product.getIn([FORM.PHOTOS, VALUE, 0, 'alt_text']) !== oldProduct.getIn([FORM.PHOTOS, VALUE, 0, 'alt_text'])
      ) {
        yield function effect(dispatch) {
          const { request } = getAIRequest({ attribute: ATTRIBUTE.KEYWORDS, product: applied.product, shop });
          const controller = createController();
          const { signal } = controller.start();
          const path = ['generating', ATTRIBUTE.KEYWORDS, shopId, request.key];

          dispatch(actions.data.setData({ path, value: controller }));

          function onFail(error) {
            if (signal.aborted) return;

            controller.stop();
            dispatch(actions.data.setData({ path }));
            dispatch(actions.edit.setProfile.rejected({ error, session, shopId, signal: payload.signal }));
          }

          function onSuccess(response) {
            controller.stop();
            dispatch(actions.data.setData({ path }));

            const keywords = shapeAIResponseForApp({ attribute: ATTRIBUTE.KEYWORDS, channel, response });
            const updates = editKeywords({ product: applied.product, value: keywords });
            updates.product = updateListingScore({
              product: updates.product.set('operations',
                updates.product.get('operations', EMPTY.LIST).concat(updates.operations)
              ),
              shop,
              userFeatures,
            });

            dispatch(
              actions.edit.setProfile.fulfilled({
                applied: { ...applied, product: updates.product },
                session,
                shopId,
                signal: payload.signal,
                type: PROFILE_TYPE.STANDARD,
              })
            );
          }

          api.ai.generateText(request)
            .then(onSuccess)
            .catch(onFail);
        };

        return reduction;
      }

      const products = edit.get('products').set(shopId, applied.product);
      return reduction.set('edit',
        clearCategoriesToLoad(
          edit
            .set('products', products)
            .set('isComplete', areComplete(products))
        )
      );
    }

    default: {
      return reduction;
    }
  }
}

function* setProfileRejected(reduction, { error, session, shopId, signal }) {
  if (signal.aborted || reduction.getIn(['edit', 'session']) !== session) return reduction;

  abort(profileController);

  yield function effect(dispatch) {
    console.error('Error encountered while getting profile: ', error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_PROFILE,
      })
    );
  };

  return reduction.setIn(['edit', 'products', shopId], reduction.getIn(['edit', 'products', shopId, 'oldProduct']));
}

function* toggleShop(reduction, shopId) {
  const edit = reduction.get('edit');
  const isNew = edit.get('productId') === NEW;
  const connected = edit.hasIn(['products', shopId]);
  const targetShop = reduction.getIn(['shops', 'byId', shopId]);

  yield function effect(dispatch) {
    function openModal() {
      dispatch(actions.edit.setModal({ type: MODALS.COPY_CONTENT, targetShop }));
    }

    if (isNew) {
      if (connected) {
        dispatch(actions.edit.removeNew(shopId));
      } else if (getSize(edit.get('products'))) {
        openModal();
      } else {
        dispatch(actions.edit.createNew({ shopId }));
      }

      return;
    }

    if (connected) {
      if (edit.getIn(['products', shopId, 'productId']) === NEW) {
        dispatch(actions.edit.removeNew(shopId));
      }
    } else if (getSize(edit.get('products'))) {
      openModal();
    } else {
      dispatch(actions.edit.createNew({ shopId }));
    }
  };

  return reduction;
}

function* updateProductAttributes(state, { shopId }) {
  yield function effect(dispatch) {
    const edit = state.get('edit', EMPTY.MAP);

    if (!edit.hasIn(['products', shopId])) return;

    let product = edit.getIn(['products', shopId]).delete('loading');
    const channel = product.get('channel');
    const userFeatures = state.getIn(['user', 'features']);
    const shopData = state.getIn(['data', 'shopsData', shopId], EMPTY.MAP);
    const shop = state.getIn(['shops', 'byId', shopId]);
    const marketplaceId = shop.get('marketplaceId');
    const category = channel === EBAY
      ? product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE])
      : product.getIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, VALUE]);

    const itemSpecifics = product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS], EMPTY.MAP);

    product = product.setIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS],
      updateItemSpecificsForCategory({ category, channel, itemSpecifics, marketplaceId })
    );

    const productDetails = product.getIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_DETAILS], EMPTY.MAP);

    product = product.setIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_DETAILS],
      updateProductDetailsForCategory({ category, channel, marketplaceId, productDetails })
    );

    dispatch(
      actions.edit.setProduct({
        shopId,
        product: setProductErrors({ product, shop, shopData, userFeatures }),
      })
    );
  };

  return state;
}

const edit = {
  [actions.edit.addPlaceholdersForNewListings]: addPlaceholdersForNewListings,
  [actions.edit.cancelEdit]: cancelEdit,
  [actions.edit.createNew]: createNew,
  [actions.edit.getData]: getData,
  [actions.edit.getLinkedProducts]: getLinkedProducts,
  [actions.edit.getLinkedProducts.fulfilled]: getLinkedProductsFulfilled,
  [actions.edit.getLinkedProducts.rejected]: getLinkedProductsRejected,
  [actions.edit.getProduct]: getProduct,
  [actions.edit.getProductData]: getProductData,
  [actions.edit.getProductData.fulfilled]: getProductDataFulfilled,
  [actions.edit.getProductData.rejected]: getProductDataRejected,
  [actions.edit.getProfileListings]: getProfileListings,
  [actions.edit.getProfileListings.fulfilled]: getProfileListingsFulfilled,
  [actions.edit.getProfileListings.rejected]: getProfileListingsRejected,
  [actions.edit.removeNew]: removeNew,
  [actions.edit.saveAs]: saveAs,
  [actions.edit.saveNew]: saveNew,
  [actions.edit.saveNew.rejected]: saveNewRejected,
  [actions.edit.saveNew.fulfilled]: saveNewFulfilled,
  [actions.edit.saveToChannels]: saveToChannels,
  [actions.edit.saveToChannels.fulfilled]: saveToChannelsFulfilled,
  [actions.edit.saveToChannels.rejected]: saveToChannelsRejected,
  [actions.edit.setData]: setData,
  [actions.edit.setModal]: setModal,
  [actions.edit.setProduct]: setProduct,
  [actions.edit.setProfile]: setProfile,
  [actions.edit.setProfile.fulfilled]: setProfileFulfilled,
  [actions.edit.setProfile.rejected]: setProfileRejected,
  [actions.edit.toggleShop]: toggleShop,
  [actions.edit.updateProductAttributes]: updateProductAttributes,
};

export default edit;
